import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Header } from '@/components/ui/header'
import { GoogleIcon } from './google-icon'
import { MapPin, Star, Check, Loader2, Search, AlertTriangle } from 'lucide-react'
import { Input } from '@/components/ui/input'

export interface Location {
  id: string
  name: string
  address: string
  rating: number
  reviewCount: number
  imageUrl?: string
}

interface LocationSelectorProps {
  locations: Location[]
  isLoading?: boolean
  onSaveLocations: (locationIds: string[]) => void
  isSaving?: boolean
  dataSource?: 'google' | 'savoria'
}

export function LocationSelector({
  locations,
  isLoading,
  onSaveLocations,
  isSaving,
  dataSource = 'google',
}: LocationSelectorProps) {
  const { t } = useTranslation()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const filteredLocations = locations.filter(
    (loc) =>
      loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.address.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const toggleLocation = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
  }

  const toggleAll = () => {
    if (selectedIds.length === filteredLocations.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(filteredLocations.map((loc) => loc.id))
    }
  }

  const handleContinue = () => {
    onSaveLocations(selectedIds)
  }

  return (
    <div className="min-h-screen bg-warm-gray">
      <Header
        rightContent={
          dataSource === 'google' ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <GoogleIcon className="w-4 h-4" />
              <span>{t('onboarding.connected')}</span>
              <Check className="w-4 h-4 text-accent" />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <AlertTriangle className="w-4 h-4" />
              <span>{t('onboarding.limitedMode')}</span>
            </div>
          )
        }
      />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          {dataSource === 'savoria' && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {t('onboarding.selectorAggregatedTitle')}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {t('onboarding.selectorAggregatedDesc')}
                </p>
              </div>
            </div>
          )}

          {/* Title Section */}
          <div className="text-center space-y-2">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('onboarding.selectLocationsTitle')}
            </h1>
            <p className="text-gray-500 max-w-md mx-auto">
              {dataSource === 'google'
                ? t('onboarding.selectLocationsSubtitleGoogle')
                : t('onboarding.selectLocationsSubtitleSavoria')}
            </p>
          </div>

          {/* Search and Select All */}
          {!isLoading && locations.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('onboarding.searchLocationsPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleAll}
                className="shrink-0 bg-transparent"
              >
                {selectedIds.length === filteredLocations.length && filteredLocations.length > 0
                  ? t('onboarding.deselectAll')
                  : t('onboarding.selectAll')}
              </Button>
            </div>
          )}

          {/* Location Cards */}
          <div className="space-y-3">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16 space-y-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-gray-500">
                  {dataSource === 'google'
                    ? t('onboarding.fetchingLocations')
                    : t('onboarding.searchingDatabase')}
                </p>
              </div>
            ) : filteredLocations.length === 0 ? (
              <div className="text-center py-16 space-y-2">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto" />
                <p className="text-gray-500">
                  {searchQuery
                    ? t('onboarding.noLocationsMatch')
                    : t('onboarding.noLocationsFound')}
                </p>
              </div>
            ) : (
              filteredLocations.map((location) => (
                <LocationCard
                  key={location.id}
                  location={location}
                  isSelected={selectedIds.includes(location.id)}
                  onToggle={() => toggleLocation(location.id)}
                  dataSource={dataSource}
                />
              ))
            )}
          </div>

          {/* Footer Actions */}
          {!isLoading && locations.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                {t('onboarding.locationsSelected', {
                  selected: selectedIds.length,
                  count: locations.length,
                })}
              </p>
              <Button
                onClick={handleContinue}
                disabled={selectedIds.length === 0 || isSaving}
                size="lg"
                className="w-full sm:w-auto min-w-[200px]"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('onboarding.continueWithLocations', { count: selectedIds.length })
                )}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

function LocationCard({
  location,
  isSelected,
  onToggle,
  dataSource = 'google',
}: {
  location: Location
  isSelected: boolean
  onToggle: () => void
  dataSource?: 'google' | 'savoria'
}) {
  const { t } = useTranslation()
  return (
    <div
      onClick={onToggle}
      className={`
        relative flex items-start gap-4 p-4 rounded-xl bg-white border-2 cursor-pointer
        transition-all duration-200
        ${isSelected ? 'border-primary bg-primary/[0.02] shadow-sm' : 'border-transparent hover:border-border'}
      `}
    >
      {/* Checkbox */}
      <div className="pt-1">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
      </div>

      {/* Location Image */}
      <div className="w-16 h-16 rounded-lg bg-muted flex-shrink-0 overflow-hidden">
        {location.imageUrl ? (
          <img
            src={location.imageUrl}
            alt={location.name}
            width={64}
            height={64}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-6 h-6 text-gray-400" />
          </div>
        )}
      </div>

      {/* Location Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 truncate">{location.name}</h3>
        <p className="text-sm text-gray-500 truncate">{location.address}</p>
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="text-sm font-medium text-gray-900">
              {formatNumber(location.rating, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            </span>
          </div>
          <span className="text-sm text-gray-500">
            {t('onboarding.reviewsCount', { count: location.reviewCount })}
          </span>
          {dataSource === 'savoria' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {t('onboarding.savoriasDataBadge')}
            </span>
          )}
        </div>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4">
          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
            <Check className="w-4 h-4 text-primary-foreground" />
          </div>
        </div>
      )}
    </div>
  )
}
